<script setup lang="ts">
import { computed, ref, watch, watchPostEffect } from 'vue';
import { SearchBar } from '@neon-one/neon-circuit';
import Message from 'primevue/message';
import RadioButton from 'primevue/radiobutton';

import { LotKindSchema, lotKeyOf, type LotRef } from '@neon-one/auctions-types';

import { useSettlementStore } from '../../store';
import { formatCents } from '../../format';
import { nonBidderPriceCents } from '../../reassign-consequences';
import { ReassignMode, type ReassignModeValue } from '../../item-actions';
import { type TargetRow, toTargetRow } from '../../reassign-target';

const props = withDefaults(defineProps<{
    open: boolean;
    lineId: string | null;
    lotRef: LotRef | null;
    fmvCents: number | null;
    lineAmountCents: number;
    modelValue: TargetRow | null;
    mode?: ReassignModeValue;
    unsold?: boolean;
    enhanced?: boolean;
}>(), { mode: ReassignMode.SendOffer, unsold: false, enhanced: false });

const emit = defineEmits<{ 'update:modelValue': [TargetRow | null] }>();

const store = useSettlementStore();
const fmt = formatCents;

const SEARCH_MAX_LENGTH = 120;
const LoadState = { Loading: 'loading', Error: 'error' } as const;
const searchRoot = ref<HTMLElement | null>(null);

watchPostEffect(() => {
    // Circuit does not forward maxlength to its nested input.
    searchRoot.value?.querySelector('input')?.setAttribute('maxlength', String(SEARCH_MAX_LENGTH));
});

const search = ref('');

const targets = computed(() => store.reassignTargetsFor(props.lotRef));
const noun = computed(() => props.lotRef?.kind ?? LotKindSchema.enum.item);
const legend = computed(() => props.unsold
    ? props.mode === ReassignMode.SendOffer ? 'Offer to' : 'Sell to'
    : 'Reassign to');
const targetsLoading = computed(() => targets.value === null && store.targetsState === LoadState.Loading);
const targetsFailed = computed(() => targets.value === null && store.targetsState === LoadState.Error);

// What a candidate who never bid on this lot is charged: the value recorded at
// close, or the departing holder's line amount when none was. Mirrors the
// server's own fallback — a number on screen that differs from the number
// charged is a money lie.
const inheritedPriceCents = computed(() =>
    nonBidderPriceCents({ fmvCents: props.fmvCents, lineAmountCents: props.lineAmountCents }),
);

const toRow = (target: Parameters<typeof toTargetRow>[0]): TargetRow =>
    toTargetRow(target, inheritedPriceCents.value);

// The lot's own bidders, at their own best surviving bid, highest first.
const stack = computed<TargetRow[]>(() =>
    (targets.value ?? [])
        .filter((t) => t.amountCents !== null)
        .map(toRow)
        .sort((a, b) => b.amountCents - a.amountCents),
);

// Everyone else registered to the event, A→Z on the name as rendered.
const otherTargets = computed<TargetRow[]>(() =>
    (targets.value ?? [])
        .filter((t) => t.amountCents === null)
        .map(toRow)
        .sort((a, b) => a.name.localeCompare(b.name)),
);

const allTargets = computed<TargetRow[]>(() => [...stack.value, ...otherTargets.value]);
const poolIsEmpty = computed(() => targets.value !== null && allTargets.value.length === 0);

// Always offered, never gated on pool size. A threshold means the control an
// operator reaches for is present or absent depending on how many bidders this
// particular lot happens to have, which is not something they can predict.
const showSearch = computed(() => allTargets.value.length > 0);
const matching = (rows: TargetRow[]): TargetRow[] => {
    const q = search.value.trim().toLowerCase();
    return q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
};
const visibleStack = computed(() => matching(stack.value));
const visibleOthers = computed(() => matching(otherTargets.value));
// Preselected in the common case; labelled so a filtered view can't hang the
// caption on whichever row happens to be first.
const nextHighestUuid = computed(() => stack.value[0]?.bidderUuid ?? null);

const selectedUuid = computed(() => props.modelValue?.bidderUuid ?? null);

// Resolved against the FULL pool, not the filtered view: typing a search that
// excludes the chosen row must not silently drop the selection.
function select(bidderUuid: string): void {
    emit('update:modelValue', allTargets.value.find((c) => c.bidderUuid === bidderUuid) ?? null);
}

// The pool is per lot and only the open dialog reads it, so it is fetched on
// open rather than with the roster — loading every lot's candidates would pull
// the whole event to render one dialog.
watch(
    [() => props.open, () => (props.lotRef ? lotKeyOf(props.lotRef) : null)],
    ([open]) => {
        const lot = props.lotRef;
        if (!open || lot === null) return;
        void store.loadReassignTargets(lot);
    },
    { immediate: true },
);

// Reset when the dialog opens AND when the lot changes while it is open — a lot
// that arrives (or is swapped) after open would otherwise keep a selection made
// for a different lot's stack.
watch([() => props.open, () => props.lineId, () => props.lotRef ? lotKeyOf(props.lotRef) : null], ([open]) => {
    if (!open) return;
    search.value = '';
    emit('update:modelValue', null);
});

// The next-highest under-bidder is the common case, so it is preselected — but
// the pool arrives after the dialog opens, so this cannot happen in the reset
// above. Only ever fills an empty selection: a choice the admin has already
// made must survive a background refetch.
watch(nextHighestUuid, (uuid) => {
    if (props.open && uuid && selectedUuid.value === null) select(uuid);
});
</script>

<template>
    <fieldset class="ri__group">
        <legend class="ri__legend">{{ legend }}</legend>

        <div v-if="targetsLoading" class="ri__muted text-caption" data-testid="ri__loading">
            Reading who can take this {{ noun }}…
        </div>

        <!-- A failed read must never read as "there is nobody": every registered
                  bidder is a valid target, so an unannounced empty pool hides real
                  choices on a money action. -->
        <Message
            v-else-if="targetsFailed"
            severity="error"
            :closable="false"
            data-testid="ri__targets-error"
        >
            We couldn't load who this {{ noun }} can go to. Close this and try again.
        </Message>

        <!-- A pool that loaded and holds nobody is a real answer, not a blank list.
                  Left unsaid it reads as a broken dialog. -->
        <Message v-else-if="poolIsEmpty" severity="warn" :closable="false" data-testid="ri__no-targets">
            <template v-if="unsold">
                No other bidder is registered to this event, so there is nobody to place this {{ noun }} with.
            </template>
            <template v-else>
                No other bidder is registered to this event, so there is nobody to reassign this {{ noun }} to.
            </template>
        </Message>

        <div v-if="showSearch" ref="searchRoot" data-testid="ri__search">
            <SearchBar v-model="search" placeholder="Search bidders" :debounce="false" />
        </div>

        <template v-if="visibleStack.length">
            <span v-if="otherTargets.length" class="ri__legend">Bid on this {{ noun }}</span>
            <div
                v-for="c in visibleStack"
                :key="c.bidderUuid"
                class="app-checkbox-group ri__row"
                :class="{ 'ri__row--selected': c.bidderUuid === selectedUuid }"
                :data-testid="`ri__bidder-${c.bidderUuid}`"
                @click.self="select(c.bidderUuid)"
            >
                <RadioButton
                    :model-value="selectedUuid"
                    :value="c.bidderUuid"
                    :input-id="`ri-bidder-${c.bidderUuid}`"
                    name="ri-bidder"
                    @update:model-value="select(c.bidderUuid)"
                />
                <label
                    class="app-field-label ri__row-label"
                    :for="`ri-bidder-${c.bidderUuid}`"
                >
                    <span class="ri__row-text">
                        <span class="ri__row-name">{{ c.name }}</span>
                    </span>
                    <span v-if="c.bidderUuid === nextHighestUuid" class="ri__row-caption text-caption">
                        Next highest
                    </span>
                    <span v-if="!enhanced" class="ri__row-amount">{{ fmt(c.amountCents) }}</span>
                </label>
            </div>
        </template>

        <template v-if="visibleOthers.length">
            <span class="ri__legend">{{ enhanced && !stack.length ? 'Registered bidders' : 'Other registered bidders' }}</span>
            <div class="ri__group ri__pool" data-testid="ri__pool">
                <div
                    v-for="c in visibleOthers"
                    :key="c.bidderUuid"
                    class="app-checkbox-group ri__row"
                    :class="{ 'ri__row--selected': c.bidderUuid === selectedUuid }"
                    :data-testid="`ri__bidder-${c.bidderUuid}`"
                    @click.self="select(c.bidderUuid)"
                >
                    <RadioButton
                        :model-value="selectedUuid"
                        :value="c.bidderUuid"
                        :input-id="`ri-bidder-${c.bidderUuid}`"
                        name="ri-bidder"
                        @update:model-value="select(c.bidderUuid)"
                    />
                    <label
                        class="app-field-label ri__row-label"
                        :for="`ri-bidder-${c.bidderUuid}`"
                    >
                        <span class="ri__row-text">
                            <span class="ri__row-name">{{ c.name }}</span>
                        </span>
                        <span class="ri__row-caption text-caption">No bid</span>
                        <span v-if="!enhanced" class="ri__row-amount">{{ fmt(c.amountCents) }}</span>
                    </label>
                </div>
            </div>
        </template>
    </fieldset>
</template>

<style scoped>
.ri__group {
    margin: 0;
    padding: 0;
    border: none;
    display: flex;
    flex-direction: column;
    gap: var(--app-space-8);
}
.ri__muted {
    margin: 0;
    color: var(--app-text-muted);
}
/* An event roster runs to hundreds of bidders. Scroll the pool rather than let
      it grow the dialog: the confirm button has to stay reachable without the
      list being truncated to whatever fits. §5 target pool */
.ri__pool {
    max-height: 30vh;
    overflow-y: auto;
}
.ri__legend {
    padding: 0;
    margin-bottom: var(--app-space-8);
    font-size: var(--app-font-size-body-sm);
    font-weight: var(--app-font-weight-semibold);
    color: var(--app-text-primary);
}
.ri__row {
    display: flex;
    align-items: center;
    gap: var(--app-space-12);
    padding: var(--app-space-12);
    border: var(--app-border-width-default) solid var(--app-border-muted);
    border-radius: var(--app-radius-md);
    cursor: pointer;
}
.ri__row-label {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: var(--app-space-12);
    cursor: pointer;
}
/* §5.3 selected target row */
.ri__row--selected {
    border-color: var(--app-border-active);
    background: var(--app-highlight-background);
}
.ri__row-text {
    display: flex;
    flex-direction: column;
    gap: var(--app-space-4);
    flex: 1;
    min-width: 0;
}
.ri__row-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.ri__row-caption {
    color: var(--app-text-primary);
    flex-shrink: 0;
}
.ri__row-amount {
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
}
</style>
